// `routes`: Swift `Route` / `RouteStop` (GroundTeam/Models/Route.swift). Read-only on the web (WEB_PLAN §2.3): routes
// are planned and driven on the phone, the console only shows them.
//
// Decoding template `Route(name: "", stops: [RouteStop(recordId: UUID(), order: 0)])`
// (GroundTeam/Sync/ResilientDecoding.swift:116). Each stored stop is merged over that template stop, so a stop
// missing a key takes the template's value, including a random `id` / `recordId` (a fresh UUID, which is what an
// iPhone ends up with too). A payload with no `stops` key reads as that single template stop. Both are mirrored.

import { ROUTE_STATUSES, type RouteStatus } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import { COORDINATE, type CoordinatePayload } from './common';
import {
  BOOL,
  DATE,
  DOUBLE,
  INT,
  STRING,
  UUID,
  arrayOf,
  createModel,
  decodeModel,
  enumOf,
  field,
  nowWire,
  objectOf,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface RouteStopPayload extends Passthrough {
  id: string;
  recordId: string;
  /** Int. */
  order: number;
  plannedArrival?: string;
  arrivedAt?: string;
  completedAt?: string;
  skipped: boolean;
  notes: string;
}

export const ROUTE_STOP_KEYS = [
  'id',
  'recordId',
  'order',
  'plannedArrival',
  'arrivedAt',
  'completedAt',
  'skipped',
  'notes',
] as const satisfies readonly (keyof Known<RouteStopPayload>)[];

export const ROUTE_STOP_FIELDS: FieldMap<RouteStopPayload> = {
  id: field.req(UUID, newId),
  recordId: field.req(UUID, newId),
  order: field.req(INT, () => 0),
  plannedArrival: field.opt(DATE),
  arrivedAt: field.opt(DATE),
  completedAt: field.opt(DATE),
  skipped: field.req(BOOL, () => false),
  notes: field.req(STRING, () => ''),
};

export const ROUTE_STOP = objectOf<RouteStopPayload>('RouteStop', ROUTE_STOP_FIELDS);

/** Reads one stop as an element of `stops` (merged over the template stop). */
export function decodeRouteStop(raw: JsonObject): Decoded<RouteStopPayload> {
  return decodeModel(ROUTE_STOP_FIELDS, raw, { hasId: false });
}

export type RouteStopInit = Partial<Known<RouteStopPayload>> & { recordId: string; order: number };

export function newRouteStop(init: RouteStopInit): RouteStopPayload {
  return createModel(ROUTE_STOP_FIELDS, { id: newId(), skipped: false, notes: '' }, init, 'stop');
}

/** `RouteStop.isDone`. */
export function isStopDone(s: Pick<RouteStopPayload, 'completedAt' | 'skipped'>): boolean {
  return s.completedAt !== undefined || s.skipped;
}

export interface RoutePayload extends Passthrough {
  id: string;
  name: string;
  date: string;
  ownerId?: string;
  stops: RouteStopPayload[];
  start?: CoordinatePayload;
  end?: CoordinatePayload;
  status: RouteStatus;
  isOptimized: boolean;
  totalMeters: number;
  estimatedSeconds: number;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
}

export const ROUTE_KEYS = [
  'id',
  'name',
  'date',
  'ownerId',
  'stops',
  'start',
  'end',
  'status',
  'isOptimized',
  'totalMeters',
  'estimatedSeconds',
  'startedAt',
  'completedAt',
  'createdAt',
] as const satisfies readonly (keyof Known<RoutePayload>)[];

export const ROUTE_FIELDS: FieldMap<RoutePayload> = {
  id: field.strict(UUID),
  name: field.req(STRING, () => ''),
  date: field.req(DATE, nowWire),
  ownerId: field.opt(UUID),
  stops: field.req(arrayOf(ROUTE_STOP), () => [
    { id: newId(), recordId: newId(), order: 0, skipped: false, notes: '' },
  ]),
  start: field.opt(COORDINATE),
  end: field.opt(COORDINATE),
  status: field.req(enumOf('RouteStatus', ROUTE_STATUSES), () => 'planned'),
  isOptimized: field.req(BOOL, () => false),
  totalMeters: field.req(DOUBLE, () => 0),
  estimatedSeconds: field.req(DOUBLE, () => 0),
  startedAt: field.opt(DATE),
  completedAt: field.opt(DATE),
  createdAt: field.req(DATE, nowWire),
};

export function decodeRoute(raw: JsonObject, envelopeId?: string): Decoded<RoutePayload> {
  return decodeModel(ROUTE_FIELDS, raw, {}, envelopeId);
}

export type RouteInit = Omit<Partial<Known<RoutePayload>>, 'stops'> & {
  name: string;
  /** Each stop is completed with the RouteStop defaults (`newRouteStop`). */
  stops?: RouteStopInit[];
};

/** A new route (the property defaults: no stops, planned, dated now). Stops are completed through `newRouteStop`. */
export function newRoute(init: RouteInit): RoutePayload {
  const now = nowWire();
  const { stops, ...rest } = init;
  const defaults = {
    id: newId(),
    date: now,
    stops: (stops ?? []).map((s) => newRouteStop(s)),
    status: 'planned',
    isOptimized: false,
    totalMeters: 0,
    estimatedSeconds: 0,
    createdAt: now,
  };
  return createModel(ROUTE_FIELDS, defaults, rest, 'routes');
}

/** `Route.orderedStops`. */
export function orderedStops(r: Pick<RoutePayload, 'stops'>): RouteStopPayload[] {
  return [...r.stops].sort((a, b) => a.order - b.order);
}
